import type { IMember } from '@/interfaces/IMember';
import { MemberRole } from '@/interfaces/IMember';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import type { IRestaurantSettings } from '@/api/services/restaurants.service';
import type { IBillingSubscriptionSummaryResponse } from '@/api/services/billing.service';
import { parseDateSafe } from '@/utils/billing-entitlements.utils';

export type TPlanNoticeKind =
  | 'legacy_expiring'
  | 'subscription_cancelled_expiring';

/** How urgent a plan notice looks (V2ShellNotice tone). */
export type TPlanNoticeTone = 'warning' | 'danger';

export interface IPlanNotice {
  kind: TPlanNoticeKind;
  expiresAtMs: number;
  diffDays: number;
  /** `danger` on the last day, `warning` before. */
  tone: TPlanNoticeTone;
  /** i18n keys (`shell.planNotice.*`); the title is plural on `diffDays`. */
  titleKey: string;
  subtitleKey: string;
  actionKey: string;
  helpKey: string;
}

export type TBillingSubscriptionLoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

export function computePlanNotice(input: {
  restaurant: IRestaurant | null | undefined;
  role: IMember | null | undefined;
  effectiveTier: number;
  restaurantSettings: IRestaurantSettings | null | undefined;
  subscription: IBillingSubscriptionSummaryResponse | null | undefined;
  subscriptionStatus: TBillingSubscriptionLoadStatus | null | undefined;
  nowMs?: number;
}): IPlanNotice | null {
  const restaurant = input.restaurant ?? null;
  const role = input.role ?? null;
  const nowMs = input.nowMs ?? Date.now();
  const subscription = input.subscription ?? null;
  const subscriptionStatus = input.subscriptionStatus ?? 'idle';

  if (!restaurant) return null;
  if (!role) return null;
  if (role.type === MemberRole.Custom) return null;
  if (input.effectiveTier <= 0) return null;

  const expiresAt = parseDateSafe(restaurant.planExpiresAt);
  if (!expiresAt) return null;

  const expiresAtMs = expiresAt.getTime();
  if (!Number.isFinite(expiresAtMs)) return null;

  const dayMs = 1000 * 60 * 60 * 24;
  const diffMs = expiresAtMs - nowMs;
  const diffDays = Math.max(0, Math.round(diffMs / dayMs));

  const tone: TPlanNoticeTone = diffDays > 1 ? 'warning' : 'danger';

  const legacyWarningDays = (() => {
    const hasLegacyAutoRenew =
      !!input.restaurantSettings?.paymentData && !!input.restaurantSettings?.autoRenew;
    return hasLegacyAutoRenew ? 2 : 7;
  })();

  const isWithin = (warnDays: number) => diffMs <= warnDays * dayMs;

  // If we don't have subscription info yet and it's currently being loaded,
  // avoid showing a potentially wrong legacy message (prevents flicker for auto-renew subs).
  if (!subscription && (subscriptionStatus === 'idle' || subscriptionStatus === 'loading')) {
    return null;
  }

  const subscriptionAutoRenews =
    !!subscription &&
    subscription.status === 'ACTIVE' &&
    subscription.cancelAtPeriodEnd === false;

  if (subscriptionAutoRenews) return null;

  const subscriptionEnds =
    !!subscription &&
    ((subscription.status === 'ACTIVE' && subscription.cancelAtPeriodEnd === true) ||
      subscription.status === 'CANCELLED');

  if (subscriptionEnds && isWithin(7)) {
    return {
      kind: 'subscription_cancelled_expiring',
      expiresAtMs,
      diffDays,
      tone,
      titleKey:
        diffDays > 0
          ? 'shell.planNotice.subscriptionEndsIn'
          : 'shell.planNotice.subscriptionEndsToday',
      subtitleKey: 'shell.planNotice.subscriptionSubtitle',
      actionKey: 'shell.planNotice.subscriptionAction',
      helpKey: 'shell.planNotice.subscriptionHelp',
    };
  }

  // Legacy notice (only for restaurants not auto-renewing via subscriptions).
  if (isWithin(legacyWarningDays)) {
    return {
      kind: 'legacy_expiring',
      expiresAtMs,
      diffDays,
      tone,
      titleKey:
        diffDays > 0
          ? 'shell.planNotice.planEndsIn'
          : 'shell.planNotice.planEndsToday',
      subtitleKey: 'shell.planNotice.planSubtitle',
      actionKey: 'shell.planNotice.planAction',
      helpKey: 'shell.planNotice.planHelp',
    };
  }

  return null;
}

